import { useState, useEffect } from "react";
import { API_HOST } from "../constants";
import type { Course } from "../types";

// Кеш для курсов
const coursesCache: { courses: Course[] | null; timestamp: number } = {
  courses: null,
  timestamp: 0,
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 минут

export const useCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourses = async () => {
      // Проверяем кеш
      const now = Date.now();
      if (coursesCache.courses && (now - coursesCache.timestamp) < CACHE_DURATION) {
        setCourses(coursesCache.courses);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_HOST}/api/content/courses?published_only=true`);
        
        if (!response.ok) {
          throw new Error("Не удалось загрузить курсы");
        }

        const coursesList = await response.json();
        
        // Преобразуем в формат Course
        const formattedCourses: Course[] = await Promise.all(
          coursesList.map(async (course: any) => {
            // Загружаем полную информацию о курсе
            const courseResponse = await fetch(`${API_HOST}/api/content/courses/${course.slug}`);
            if (!courseResponse.ok) {
              throw new Error(`Не удалось загрузить курс ${course.slug}`);
            }
            const courseData = await courseResponse.json();

            return {
              slug: courseData.slug,
              title: courseData.title,
              subtitle: courseData.subtitle,
              level: courseData.level,
              description: courseData.description,
              requirements: courseData.requirements || [],
              prerequisites: courseData.prerequisites || [],
              modules: courseData.modules || [],
              graph: courseData.graph, // Опционально, может быть undefined
              coverImage: courseData.cover_image || courseData.coverImage || undefined,
              durationHours: courseData.duration_hours ?? courseData.durationHours ?? undefined,
              targetLevel: courseData.target_level ?? courseData.targetLevel ?? undefined,
            };
          })
        );

        // Обновляем кеш
        coursesCache.courses = formattedCourses;
        coursesCache.timestamp = now;

        setCourses(formattedCourses);
        setError(null);
      } catch (err) {
        console.error("Ошибка загрузки курсов:", err);
        setError(err instanceof Error ? err.message : "Неизвестная ошибка");
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  return { courses, loading, error };
};

// Хук для получения курса по slug
const courseCache: Record<string, { course: Course | null; timestamp: number }> = {};
const courseFetchPromises: Record<string, Promise<Course>> = {}; // Защита от параллельных запросов

export const useCourse = (slug: string | undefined) => {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setCourse(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchCourse = async () => {
      // Проверяем кеш
      const cached = courseCache[slug];
      const now = Date.now();
      if (cached?.course && (now - cached.timestamp) < CACHE_DURATION) {
        if (isMounted) {
          setCourse(cached.course);
          setLoading(false);
        }
        return;
      }

      // Если уже есть запрос для этого slug, ждем его
      if (courseFetchPromises[slug]) {
        try {
          const cachedCourse = await courseFetchPromises[slug];
          if (isMounted) {
            setCourse(cachedCourse);
            setLoading(false);
          }
          return;
        } catch {
          // Продолжаем с новым запросом, но удаляем промис
          delete courseFetchPromises[slug];
        }
      }

      try {
        if (isMounted) {
          setLoading(true);
        }

        // Создаем промис для загрузки курса
        const fetchPromise = (async () => {
          const response = await fetch(`${API_HOST}/api/content/courses/${slug}`);
          
          if (!response.ok) {
            if (response.status === 404) {
              throw new Error("Курс не найден");
            }
            throw new Error("Не удалось загрузить курс");
          }

          const courseData = await response.json();
          
          const formattedCourse: Course = {
            slug: courseData.slug,
            title: courseData.title,
            subtitle: courseData.subtitle,
            level: courseData.level,
            description: courseData.description,
            requirements: courseData.requirements || [],
            prerequisites: courseData.prerequisites || [],
            modules: courseData.modules || [],
            graph: courseData.graph, // Опционально, может быть undefined
          };

          // Обновляем кеш
          courseCache[slug] = { course: formattedCourse, timestamp: Date.now() };
          
          return formattedCourse;
        })();

        // Сохраняем промис ДО await, чтобы предотвратить дублирующиеся запросы
        // даже если эффект вызывается дважды в StrictMode
        courseFetchPromises[slug] = fetchPromise;

        const formattedCourse = await fetchPromise;

        // Удаляем промис после завершения
        delete courseFetchPromises[slug];

        if (isMounted) {
          setCourse(formattedCourse);
          setError(null);
        }
      } catch (err) {
        delete courseFetchPromises[slug];
        if (isMounted) {
          console.error("Ошибка загрузки курса:", err);
          setError(err instanceof Error ? err.message : "Неизвестная ошибка");
          setCourse(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCourse();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return { course, loading, error };
};
